import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Search, MoreHorizontal, Eye, Edit, FileText, ShoppingCart, Truck, PackageCheck, CheckCircle2, XCircle, FileDown, ClipboardList, IndianRupee, Package } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusConfig = {
  ACTIVE: { label: "Active", className: "bg-blue-50 text-blue-700 border-blue-200", icon: ClipboardList },
  DISPATCHED: { label: "Dispatched", className: "bg-cyan-50 text-cyan-700 border-cyan-200", icon: Truck },
  IN_TRANSIT: { label: "In Transit", className: "bg-amber-50 text-amber-700 border-amber-200", icon: Truck },
  DELIVERED: { label: "Delivered", className: "bg-teal-50 text-teal-700 border-teal-200", icon: PackageCheck },
  INSTALLED: { label: "Installed", className: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: PackageCheck },
  COMPLETED: { label: "Completed", className: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  CANCELLED: { label: "Cancelled", className: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const c = statusConfig[status] || { label: status, className: "bg-slate-50 text-slate-600 border-slate-200" };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${c.className}`}>
      {c.icon && <c.icon className="w-3 h-3" />}
      {c.label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl ${color.replace('text-', 'bg-').replace('700', '50').replace('900', '50')}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");

  const isSNP = user?.role?.startsWith("SNP_");

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, []);

  const fetchOrders = async () => {
    try {
      const r = await axios.get(`${API_URL}/purchase-orders`, getAuthHeader());
      setOrders(r.data);
    } catch { toast.error("Failed to fetch purchase orders"); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API_URL}/purchase-orders-stats`, getAuthHeader());
      setStats(r.data);
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/purchase-orders/${id}/status?status=${status}`, {}, getAuthHeader());
      toast.success(`PO status updated to ${status.replace('_', ' ')}`);
      fetchOrders();
      fetchStats();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDownloadPDF = async (id, poNumber) => {
    try {
      const response = await axios.get(`${API_URL}/purchase-orders/${id}/pdf`, {
        ...getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url; link.download = `${poNumber}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to generate PDF"); }
  };

  const filtered = orders.filter(o => {
    const matchTab = tab === "all" ||
      (tab === "active" && o.status === "ACTIVE") ||
      (tab === "transit" && ["DISPATCHED", "IN_TRANSIT"].includes(o.status)) ||
      (tab === "delivered" && ["DELIVERED", "INSTALLED", "COMPLETED"].includes(o.status));
    const matchSearch = !search ||
      o.po_number?.toLowerCase().includes(search.toLowerCase()) ||
      o.vendor_details?.name?.toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Purchase Orders</h1>
          <p className="text-slate-500 mt-1">Track and manage all purchase orders with delivery status</p>
        </div>
        <Button onClick={() => navigate("/sanction-proposals")} className="bg-slate-900 hover:bg-slate-800" data-testid="new-po-btn">
          <Plus className="w-4 h-4 mr-2" /> Create PO via Sanction Proposal
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard icon={ShoppingCart} label="Total POs" value={stats.total} color="text-slate-900" />
          <StatCard icon={ClipboardList} label="Active" value={stats.active} color="text-blue-700" />
          <StatCard icon={Truck} label="In Transit" value={stats.in_transit} color="text-amber-700" />
          <StatCard icon={PackageCheck} label="Delivered" value={stats.delivered} color="text-teal-700" />
          <StatCard icon={CheckCircle2} label="Completed" value={stats.completed} color="text-emerald-700" />
          <StatCard icon={IndianRupee} label="Total Value" value={`₹${(stats.total_value || 0).toLocaleString('en-IN')}`} color="text-slate-900" sub="excl. cancelled" />
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by PO number or vendor..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-orders" />
            </div>
            <Tabs value={tab} onValueChange={setTab} className="w-auto">
              <TabsList className="h-9">
                <TabsTrigger value="all" className="text-xs px-3">All</TabsTrigger>
                <TabsTrigger value="active" className="text-xs px-3">Active</TabsTrigger>
                <TabsTrigger value="transit" className="text-xs px-3">In Transit</TabsTrigger>
                <TabsTrigger value="delivered" className="text-xs px-3">Delivered</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1,2,3,4].map(i => <div key={i} className="flex gap-4"><Skeleton className="h-4 w-28"/><Skeleton className="h-4 flex-1"/><Skeleton className="h-4 w-20"/></div>)}
            </div>
          ) : filtered.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">PO Number</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Vendor</TableHead>
                  <TableHead className="font-semibold">PO Value</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(order => (
                  <TableRow key={order.id} className="cursor-pointer hover:bg-slate-50/50" onClick={() => navigate(`/purchase-orders/${order.id}`)} data-testid={`po-row-${order.id}`}>
                    <TableCell className="font-mono font-semibold text-slate-900 text-sm">{order.po_number}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        order.po_type === "GLOBAL" ? "border-violet-200 text-violet-700 bg-violet-50" :
                        "border-blue-200 text-blue-700 bg-blue-50"
                      }>
                        {order.po_type === "GLOBAL" ? "Global" : "Domestic"}
                        {order.po_sub_type ? " / " + order.po_sub_type : ""}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[200px] truncate">{order.vendor_details?.name || "N/A"}</TableCell>
                    <TableCell className="font-semibold text-slate-900">₹{(order.po_value || 0).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{order.po_date ? new Date(order.po_date).toLocaleDateString('en-IN') : "—"}</TableCell>
                    <TableCell><StatusBadge status={order.status} /></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/purchase-orders/${order.id}`); }}><Eye className="w-4 h-4 mr-2" />View & Track</DropdownMenuItem>
                          <DropdownMenuItem onClick={e => { e.stopPropagation(); handleDownloadPDF(order.id, order.po_number); }}><FileDown className="w-4 h-4 mr-2" />Download PDF</DropdownMenuItem>
                          {!["CANCELLED", "COMPLETED"].includes(order.status) && (
                            <DropdownMenuItem onClick={e => { e.stopPropagation(); navigate(`/purchase-orders/new?amend=${order.id}`); }} data-testid={`amend-po-${order.id}`}><Edit className="w-4 h-4 mr-2" />Amend PO</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {order.status === "ACTIVE" && <DropdownMenuItem onClick={e => { e.stopPropagation(); updateStatus(order.id, "DISPATCHED"); }}><Truck className="w-4 h-4 mr-2" />Mark Dispatched</DropdownMenuItem>}
                          {order.status === "DISPATCHED" && <DropdownMenuItem onClick={e => { e.stopPropagation(); updateStatus(order.id, "IN_TRANSIT"); }}><Truck className="w-4 h-4 mr-2" />Mark In Transit</DropdownMenuItem>}
                          {["DISPATCHED", "IN_TRANSIT"].includes(order.status) && <DropdownMenuItem onClick={e => { e.stopPropagation(); updateStatus(order.id, "DELIVERED"); }}><PackageCheck className="w-4 h-4 mr-2" />Mark Delivered</DropdownMenuItem>}
                          {order.status === "DELIVERED" && <DropdownMenuItem onClick={e => { e.stopPropagation(); updateStatus(order.id, "INSTALLED"); }}><Package className="w-4 h-4 mr-2" />Mark Installed</DropdownMenuItem>}
                          {["DELIVERED", "INSTALLED"].includes(order.status) && <DropdownMenuItem onClick={e => { e.stopPropagation(); updateStatus(order.id, "COMPLETED"); }}><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />Complete</DropdownMenuItem>}
                          {!["COMPLETED", "CANCELLED"].includes(order.status) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-rose-600" onClick={e => { e.stopPropagation(); updateStatus(order.id, "CANCELLED"); }}><XCircle className="w-4 h-4 mr-2" />Cancel PO</DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-16 text-center">
              <ShoppingCart className="w-14 h-14 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No purchase orders found</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">{search ? "Try a different search" : "Create PO after sanction proposal approval"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchaseOrders;
